/**
 * Hides the mobile action bar while the page's own version of the same
 * buttons is on screen.
 *
 * The bar repeats the hero's two calls to action, which is the point of it --
 * once the hero has scrolled away the actions are still one thumb-reach
 * away. But at the top of a phone screen the hero's own buttons and the fixed
 * bar sit roughly a hundred pixels apart, so the same pair renders twice, and
 * as the page scrolls the hero's button slides underneath the bar and shows
 * as a half-cut duplicate.
 *
 * Horizon solves the identical problem for the product page's sticky
 * add-to-cart with an IntersectionObserver, so this follows that pattern
 * rather than inventing a scroll handler.
 *
 * The observer's bottom rootMargin is pulled in by the height of the bar. A
 * button sitting *behind* the bar is not actually visible to anyone, so it
 * should not count as a duplicate and keep the bar hidden -- without the
 * negative margin the bar hides itself exactly when it is covering the thing
 * it was checking for.
 */

const HIDDEN_CLASS = 'fsf-sticky--hidden';

class FsfStickyCtaComponent extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<Element>} */
  #onScreen = new Set();

  connectedCallback() {
    const bar = this.querySelector('.fsf-sticky');
    const selector = this.dataset.watch;
    if (!bar || !selector) return;

    let targets;
    try {
      targets = document.querySelectorAll(selector);
    } catch {
      // A malformed selector typed into the theme editor should leave the bar
      // working as it did before, not throw and take the module down.
      return;
    }

    // Nothing on this template duplicates the bar, so it simply always shows.
    if (!targets.length) return;

    const barHeight = Math.round(bar.getBoundingClientRect().height) || 78;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#onScreen.add(entry.target);
          else this.#onScreen.delete(entry.target);
        }
        bar.classList.toggle(HIDDEN_CLASS, this.#onScreen.size > 0);
      },
      { rootMargin: `0px 0px -${barHeight}px 0px` }
    );

    for (const target of targets) this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#onScreen.clear();
  }
}

if (!customElements.get('fsf-sticky-cta')) {
  customElements.define('fsf-sticky-cta', FsfStickyCtaComponent);
}
